import React from 'react';
import {runInAction} from 'mobx';
import {observer} from 'mobx-react';
import s from '../../s';
import {prod_cat} from '../../api';
import {openLink, Portal} from '../../../util';
import {findCategoryOptionById, getCategoryName, getCategoryOptionKey, getCategoryOptions, isSelectedCategoryOption, shouldUseCategoryTiles} from './category-utils';
import {allowsCategoryOption} from './document-sizing';

interface ICategoryProps {
    CategoryID: number;
}

const Category: React.FC<ICategoryProps> = observer(({CategoryID}) => {
    const options = getCategoryOptions(s.prod.categories, CategoryID).filter(option => allowsCategoryOption(s.prod, option));
    const categoryName = getCategoryName(options);
    const useTiles = shouldUseCategoryTiles(options.length);

    const save = async (id: number) => {
        const cat = findCategoryOptionById(options, id);
        if (!cat || !allowsCategoryOption(s.prod, cat)) {
            return;
        }

        if (cat.LinkToProductID < 1) {
            runInAction(() => {
                s.ps.category_map_id = cat.id;
                s.ps.category_selected_no_new_product = cat.id > 0;
                const _w: number = cat.Doc_Width;
                const _h: number = cat.Doc_Height;

                if (_w <= _h) {
                    s.ps.docWidth = _w;
                    s.ps.docHeight = _h;
                    s.ps.is_horizontal = false;
                } else if (_w > _h) {
                    s.ps.docWidth = _w;
                    s.ps.docHeight = _h;
                    s.ps.is_horizontal = true;
                }
                s.prod.IsHorizontal = s.ps.is_horizontal;
                s.prod.OrientationHorizontal = s.ps.is_horizontal;
                s.ps.docWidth2 = s.ps.docWidth;
                s.ps.docHeight2 = s.ps.docHeight;
                if (s.prod.docWidth2 <= 0) {
                    s.ps.docWidth2 = -1;
                    s.ps.docHeight2 = -1;
                }

                if (s.ps.glass_id === 2) {
                    s.ps.glass_id = 4;
                    s.prod.AllowGlassID2 = 0;
                }

                if (s.prod.DYF_FrameStyleID === 2 || s.prod.DYF_FrameStyleID === 7 || s.prod.DYF_FrameStyleID === 8 || s.prod.DYF_FrameStyleID === 6) {
                    if (s.ps.docWidth > 9 && s.ps.docHeight > 12) {
                        s.ps.use_plexi = true;
                    } else if (s.ps.docWidth > 12 && s.ps.docHeight > 9) {
                        s.ps.use_plexi = true;
                    } else {
                        s.ps.use_plexi = false;
                    }
                } else if (s.prod.DYF_FrameStyleID === 3 || s.prod.DYF_FrameStyleID === 5) {
                    if (s.ps.docWidth > 11 && s.ps.docHeight > 14) {
                        s.ps.use_plexi = true;
                    } else if (s.ps.docWidth > 14 && s.ps.docHeight > 11) {
                        s.ps.use_plexi = true;
                    } else {
                        s.ps.use_plexi = false;
                    }
                } else {
                    if (s.ps.docWidth > 14 && s.ps.docHeight > 18) {
                        s.ps.use_plexi = true;
                    } else if (s.ps.docWidth > 18 && s.ps.docHeight > 14) {
                        s.ps.use_plexi = true;
                    } else {
                        s.ps.use_plexi = false;
                    }
                }

                s.ui.doRender = true;
                s.ui.update_pricing = true;
            });
            s.pulse();
        } else {
            const result = (await prod_cat(cat.LinkToProductID)) as string;
            openLink(result);
        }
    };

    if (!s.ready || !categoryName || s.ps.is_caf || (s.docsizes_enabled && s.docsizes_available)) {
        return null;
    }

    return (
        <div
            className="gray-box category-container"
            id={`category-${CategoryID}`}
        >
            <div>
                <span
                    className="header-std"
                    style={{paddingRight: '0.25rem'}}
                >
                    {categoryName}
                </span>

                {useTiles && (
                    <div
                        className="cat-prod"
                        role="listbox"
                        aria-label="Select the option of the product category"
                    >
                        {options.map(m => {
                            const selected = isSelectedCategoryOption(s.ps.category_map_id, m.id);
                            return (
                                <div
                                    key={getCategoryOptionKey(m)}
                                    title={m.Name}
                                    data-catid={`category-${CategoryID}`}
                                    aria-label={`Select the ${m.Name} option of the product category ${m.CategoryName}`}
                                    aria-selected={selected}
                                    className={`cust-entity${selected ? ' active' : ''}`}
                                    onClick={() => save(m.id)}
                                    onKeyDown={e => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            save(m.id);
                                        }
                                    }}
                                    role="option"
                                    tabIndex={0}
                                >
                                    <div className="title">
                                        {m.Name}
                                        {s.__fast_ship_orig && s.isRetail && selected && (
                                            <div className={`prod-fast-shipping fast-shipping-${Portal?.PortalID}`}>
                                                <i className={Portal?.PortalID !== 3 ? `fas fa-shipping-fast` : `fas fa-cart-shopping-fast`}></i>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                {!useTiles && (
                    <select
                        className="cust-entity form-select ib"
                        aria-label={`Select ${categoryName}`}
                        value={s.ps.category_map_id}
                        onChange={e => save(parseInt(e.target.value, 10))}
                    >
                        {options.map(m => (
                            <option
                                key={getCategoryOptionKey(m)}
                                value={m.id}
                            >
                                {m.Name}
                            </option>
                        ))}
                    </select>
                )}
            </div>
        </div>
    );
});

export default Category;
